import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import CardMenu from '../Common/CardMenu';
import Collapse from '../Common/Collapse';
import { getSections, createSection, updateSection, deleteSection, deleteLesson, reorderSections, reorderLessons, getHomeworks, createLesson, updateLesson, updateCourse, publishAllInCourse, getDeletedInCourse, restoreSection, restoreLesson, duplicateSection, duplicateLesson, makeCurrentSection } from '../../services/adminService';
import LessonEditorPane from './LessonEditorPane';
import HomeworkNumbersPane from './HomeworkNumbersPane';
import Modal from '../Common/Modal';
import Input from '../Common/Input';
import Button from '../Common/Button';
import { confirmDialog } from '../Common/confirm';
import toast from 'react-hot-toast';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCenter, useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const NEW = '__new__';
const MONTHS = ['ЯНВ','ФЕВ','МАР','АПР','МАЙ','ИЮН','ИЮЛ','АВГ','СЕН','ОКТ','НОЯ','ДЕК'];
const DAYS = ['Вс','Пн','Вт','Ср','Чт','Пт','Сб'];

const IcoLayers = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>
  </svg>
);
// Русское склонение для подписей в режиме восстановления
const plural = (n, f) => { const a = Math.abs(n) % 100, b = a % 10; if (a > 10 && a < 20) return f[2]; if (b > 1 && b < 5) return f[1]; if (b === 1) return f[0]; return f[2]; };

const IcoChevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
);
const IcoDoc = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>
);
const IcoEdit = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
);
const IcoTrash = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
);
const IcoPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
);
const IcoCal = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
);
const IcoCloud = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>
);
const IcoRestore = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l3 2"/></svg>
);
const IcoCloudUp = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/><polyline points="9 13 12 10 15 13"/><line x1="12" y1="10" x2="12" y2="17"/></svg>
);
const IcoArrowRight = () => ( // стрелка влево — режим восстановления свайпает список влево
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 5 5 12 12 19"/></svg>
);
const IcoCopy = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
);
const IcoPaste = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>
);
const IcoDup = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="8" width="12" height="12" rx="2"/><path d="M4 16V6a2 2 0 0 1 2-2h10"/></svg>
);
const IcoUp = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
);
const IcoDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
);
const IcoFlag = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
);

// Приглушённый «фон обложки курса» под модулями (минималистичные macOS-иконки)
const ShellCover = ({ id }) => (
  <svg aria-hidden="true" width="100%" height="100%" style={{ position: 'absolute', inset: 0, color: 'var(--cc-pat)', pointerEvents: 'none' }}>
    <defs>
      <pattern id={`sh-${id}`} width="130" height="130" patternUnits="userSpaceOnUse" patternTransform="rotate(-6)">
        <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.055">
          <g transform="translate(8,9)"><rect x="0" y="4" width="26" height="19" rx="4" /><path d="M0 10 h26" /></g>
          <g transform="translate(70,7)"><path d="M0 0 l14 8 l-14 8 z" /></g>
          <g transform="translate(102,10)"><circle cx="8" cy="8" r="8" /><path d="M13.7 13.7 l6 6" /></g>
          <g transform="translate(40,38)"><path d="M11 0 l11 6 v13 l-11 6 l-11 -6 v-13 z" /><path d="M0 6 l11 6 l11 -6 M11 12 v13" /></g>
          <g transform="translate(92,42)"><path d="M0 8 a14 14 0 0 1 20 0" /><path d="M4 13 a9 9 0 0 1 12 0" /><circle cx="10" cy="19" r="1.6" fill="currentColor" stroke="none" /></g>
          <g transform="translate(6,74)"><path d="M0 4 h9 l3 3 h14 a2.5 2.5 0 0 1 2.5 2.5 v11 a2.5 2.5 0 0 1 -2.5 2.5 h-26 a2.5 2.5 0 0 1 -2.5 -2.5 v-13.5 a2.5 2.5 0 0 1 2.5 -2.5 z" /></g>
          <g transform="translate(104,72)"><path d="M0 0 l6 5 l-6 5" /><path d="M10 12 h9" /></g>
          <g transform="translate(62,92)"><path d="M0 0 l0 22 l5 -6 l3.2 7.5 l3.4 -1.5 l-3.2 -7.5 l7.6 -0.8 z" /></g>
        </g>
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#sh-${id})`} />
  </svg>
);

// Дата-бейдж урока: число + месяц, или иконка календаря если даты нет
const LeafDate = ({ dateStr, sel }) => {
  if (!dateStr) return <span className={`cc-date empty${sel ? ' sel' : ''}`}><IcoCal /></span>;
  const d = new Date(dateStr);
  return (
    <span className={`cc-date${sel ? ' sel' : ''}`}>
      <b>{d.getDate()}</b>
      <i>{MONTHS[d.getMonth()]}</i>
    </span>
  );
};
// Бейдж «черновик»: облако на месте даты/иконки, когда урок не опубликован.
const LeafCloud = ({ sel }) => (
  <span className={`cc-date empty${sel ? ' sel' : ''}`} title="Черновик — не опубликовано" style={{ color: '#f59e0b' }}><IcoCloud /></span>
);
// Звезда вместо даты — у ДЗ без привязанного вебинара (даты нет; как у ученика).
const IcoStar = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
);
const LeafStar = ({ sel }) => (
  <span className={`cc-date empty${sel ? ' sel' : ''}`} title="ДЗ без привязанного вебинара"><IcoStar /></span>
);

// Дата ДЗ-блока = дата связанного вебинара; если вебинара нет — дата создания.
const hwDate = (l, lessonDateById) => (l.linkedLessonId && lessonDateById?.[l.linkedLessonId]?.date) || l.created_at;

// ── Урок/ДЗ как sortable-элемент (memo: не ре-рендерится при открытии соседних модулей/перетаскивании) ──
const LessonLeaf = React.memo(({ lesson: l, sectionId, isHw, isSel, onSelect, hwCount, lessonDateById, dragDisabled, buildLesMenu }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: 'les-' + l.id, data: { type: 'lesson', lessonId: l.id, sectionId }, disabled: dragDisabled,
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0 : 1 };
  const dateEl = !l.isPublished
    ? <LeafCloud sel={isSel} />
    : (isHw && !l.linkedLessonId)
      ? <LeafStar sel={isSel} />
      : <LeafDate dateStr={isHw ? hwDate(l, lessonDateById) : l.date} sel={isSel} />;
  const n = isHw ? (hwCount?.[l.id] || 0) : 0;
  const timeStr = !isHw && l.time ? l.time.slice(0, 5) : '';
  const d = !isHw && l.date ? new Date(l.date) : null;
  const sub = isHw
    ? `${n} ${n === 1 ? 'номер' : n > 1 && n < 5 ? 'номера' : 'номеров'}${l.linkedLessonId ? ' · привязан к уроку' : ''}`
    : (d ? `${DAYS[d.getDay()]}${timeStr ? ' · ' + timeStr : ''}` : (timeStr || 'без даты'));
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      className={`cc-leaf${isSel ? ' sel' : ''}`} onClick={() => onSelect(sectionId, l.id)}>
      {dateEl}
      <div className="cc-leaf-main">
        <div className="cc-leaf-title">{l.title}</div>
        <div className="cc-leaf-sub">{sub}</div>
      </div>
      <div className="cc-leaf-menu"><CardMenu items={buildLesMenu(l, sectionId)} /></div>
    </div>
  );
});

// ── Модуль (раздел): НЕ перетаскивается (порядок меняем кнопками в меню), но droppable —
// чтобы в него можно было перенести урок. Внутри — вложенный SortableContext уроков.
// memo: открытие одного модуля не ре-рендерит остальные (плавная анимация).
const SectionNode = React.memo(({ section, isHw, open, onToggle, selected, onSelectLesson, onAddLesson, hwCount, lessonDateById, sectionIcon, q, dragDisabled, readOnly, buildSecMenu, buildLesMenu }) => {
  const { setNodeRef } = useDroppable({ id: 'sec-' + section.id, data: { type: 'section', sectionId: section.id } });

  const all = (section.Lessons || []).filter(l => isHw ? l.isHomeworkOnly : !l.isHomeworkOnly);
  const visible = q ? all.filter(l => (l.title || '').toLowerCase().includes(q)) : all;
  const count = visible.length;
  // Облако на модуле, если сам модуль черновик ИЛИ внутри есть неопубликованный урок
  const showCloud = !section.isPublished || all.some(l => !l.isPublished);

  const node = (
    <div ref={setNodeRef} className={`cc-node${open ? ' open' : ''}${section.isCurrent ? ' current' : ''}`}>
      <div className="cc-node-head" onClick={() => onToggle(section.id)}>
        <span className="cc-node-ic" style={showCloud ? { color: '#f59e0b' } : undefined} title={showCloud ? 'Есть неопубликованное' : undefined}>
          {section.isCurrent && <span className="cc-cur-dot" />}
          {showCloud ? <IcoCloud /> : (sectionIcon || <IcoLayers />)}
        </span>
        <div className="cc-node-info">
          <div className="cc-node-title">{section.name}</div>
          <div className="cc-node-sub">{count} {isHw ? (count === 1 ? 'задание' : count > 1 && count < 5 ? 'задания' : 'заданий') : (count === 1 ? 'урок' : count > 1 && count < 5 ? 'урока' : 'уроков')}</div>
        </div>
        <div className="cc-node-actions" onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
          <CardMenu items={buildSecMenu(section)} />
        </div>
        <span className="cc-chev"><IcoChevron /></span>
      </div>

      {/* Плавное раскрытие по измеренной высоте. keepMounted — уроки уже смонтированы,
          поэтому открытие без задержки на маунт sortable-контента (иначе «долго»). */}
      <Collapse open={open} keepMounted duration={220}>
        <div className="cc-node-body">
          <SortableContext items={visible.map(l => 'les-' + l.id)} strategy={verticalListSortingStrategy}>
            {visible.map(l => (
              <LessonLeaf key={l.id} lesson={l} sectionId={section.id} isHw={isHw}
                isSel={selected?.lessonId === l.id} onSelect={onSelectLesson}
                hwCount={hwCount} lessonDateById={lessonDateById} dragDisabled={dragDisabled} buildLesMenu={buildLesMenu} />
            ))}
          </SortableContext>
          {visible.length === 0 && <div className="cc-empty-leaf">{q ? 'Ничего не найдено' : (isHw ? 'Пока нет заданий' : 'Пока нет уроков')}</div>}
          {!q && !readOnly && <button className="cc-add-leaf" onClick={() => onAddLesson(section.id)}><IcoPlus /> {isHw ? 'Добавить ДЗ' : 'Занятие'}</button>}
        </div>
      </Collapse>
    </div>
  );
  // Текущий модуль подписывать нечем: остаются фиолетовая рамка и пульсирующая точка на иконке
  return node;
});

const CourseConstructor = ({ courseId, mode = 'sections', search = '', sectionIcon, course, onCourseChanged, focusLessonId, onFocusDone }) => {
  /* Менеджер и куратор курс только смотрят: прячем всё, что меняет содержимое —
     меню модулей и занятий, кнопки добавления, публикацию и восстановление,
     а поля редактора отключаем целиком (fieldset disabled). */
  const { can } = useAuth();
  const ro = !can('courses.edit');
  const isHw = mode === 'homework';
  const q = (search || '').trim().toLowerCase();
  const [sections, setSections] = useState([]);
  const [homeworks, setHomeworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState(null);

  const [secModal, setSecModal] = useState(false);
  const [secName, setSecName] = useState('');
  const [renameTarget, setRenameTarget] = useState(null); // { kind:'section'|'lesson', id }
  const [renameVal, setRenameVal] = useState('');
  const [clipboard, setClipboard] = useState(null); // { kind, id, title }

  // Верхняя карточка курса + режим восстановления удалённых
  const [courseName, setCourseName] = useState(course?.name || '');
  const [publishing, setPublishing] = useState(false);
  // Сюда LessonEditorPane кладёт функцию «сохранить текущую форму» — её вызывает
  // кнопка «Опубликовать все», чтобы не публиковать урок со старым содержимым.
  const editorFlush = useRef(null);
  const [restoreMode, setRestoreMode] = useState(false);
  const [deleted, setDeleted] = useState({ sections: [], lessons: [] });
  useEffect(() => { setCourseName(course?.name || ''); }, [course?.name]);

  const hasDrafts = sections.some(s => !s.isPublished || (s.Lessons || []).some(l => !l.isPublished));

  const load = useCallback(async () => {
    try { const res = await getSections(courseId); setSections(res.data || []); return res.data || []; }
    catch { toast.error('Ошибка загрузки разделов'); return []; }
    finally { setLoading(false); }
  }, [courseId]);

  // Правки номеров, уже отправленные редактором на сохранение. Панель ДЗ при каждом
  // открытии собирает номера из этого списка, а автосохранение его не перечитывает —
  // без правок поверх заполненный номер после перехода к другому ДЗ открывался пустым.
  // Накладываем их и на свежий список: он мог уйти с сервера раньше сохранения.
  const savedEdits = useRef({});
  const onNumberSaved = useCallback((id, patch) => {
    savedEdits.current[id] = patch;
    setHomeworks(prev => prev.map(h => (h.id === id ? { ...h, ...patch } : h)));
  }, []);
  const loadHomeworks = useCallback(async () => {
    if (!isHw) return;
    try {
      const res = await getHomeworks();
      const edits = savedEdits.current;
      setHomeworks((res.data || []).map(h => (edits[h.id] ? { ...h, ...edits[h.id] } : h)));
    }
    catch { /* тихо */ }
  }, [isHw]);

  useEffect(() => { setLoading(true); setSelected(null); load(); loadHomeworks(); }, [load, loadHomeworks]);

  // Переход с «Главной» по конкретному уроку: раскрываем его модуль,
  // выбираем занятие и подкручиваем список к нему.
  useEffect(() => {
    if (!focusLessonId || sections.length === 0) return;
    const sec = sections.find(s => (s.Lessons || []).some(l => l.id === focusLessonId));
    if (!sec) return;
    setExpanded(prev => ({ ...prev, [sec.id]: true }));
    setSelected({ sectionId: sec.id, lessonId: focusLessonId });
    onFocusDone && onFocusDone();
    requestAnimationFrame(() => {
      document.querySelector('.cc-leaf.sel')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }, [focusLessonId, sections]); // eslint-disable-line

  const renameCourse = async () => {
    const name = courseName.trim();
    if (!name || name === course?.name) { setCourseName(course?.name || ''); return; }
    try { await updateCourse(courseId, { name }); onCourseChanged && onCourseChanged(); toast.success('Курс переименован'); }
    catch { toast.error('Ошибка переименования'); setCourseName(course?.name || ''); }
  };

  const publishAll = async () => {
    if (!hasDrafts || publishing) return;
    setPublishing(true);
    try {
      // Открытый справа урок может держать несохранённые правки формы. Сначала
      // отдаём их на сервер: иначе урок опубликуется со старым содержимым, а его
      // собственная кнопка «Опубликовать» останется активной.
      if (editorFlush.current) await editorFlush.current();
      await publishAllInCourse(courseId); await load(); toast.success('Все занятия опубликованы');
    }
    catch { toast.error('Ошибка публикации'); }
    finally { setPublishing(false); }
  };

  const loadDeleted = async () => {
    try { const r = await getDeletedInCourse(courseId); setDeleted(r.data || { sections: [], lessons: [] }); }
    catch { setDeleted({ sections: [], lessons: [] }); }
  };
  const openRestore = async () => { await loadDeleted(); setRestoreMode(true); };
  // Свёрнутость групп в режиме восстановления: по умолчанию раскрыты
  const [restoreOpen, setRestoreOpen] = useState({});
  const toggleRestore = (key) => setRestoreOpen(prev => ({ ...prev, [key]: prev[key] === false }));
  const doPurgeSection = async (sec) => {
    const n = (sec.Lessons || []).length;
    if (!(await confirmDialog({ title: 'Удалить насовсем?', message: `Модуль «${sec.name}»${n ? ` и ${n} ${plural(n, ['занятие', 'занятия', 'занятий'])} внутри` : ''} будут стёрты без возможности восстановления.`, confirmText: 'Удалить навсегда' }))) return;
    try { await deleteSection(sec.id, true); toast.success('Модуль удалён насовсем'); await loadDeleted(); await load(); }
    catch { toast.error('Не удалось удалить'); }
  };
  const doPurgeLesson = async (l) => {
    if (!(await confirmDialog({ title: 'Удалить насовсем?', message: `«${l.title}» будет стёрт без возможности восстановления.`, confirmText: 'Удалить навсегда' }))) return;
    try { await deleteLesson(l.id, true); toast.success('Удалено насовсем'); await loadDeleted(); await load(); }
    catch { toast.error('Не удалось удалить'); }
  };
  const doRestoreSection = async (id) => {
    try { await restoreSection(id); toast.success('Модуль восстановлен'); await loadDeleted(); await load(); }
    catch { toast.error('Ошибка восстановления'); }
  };
  const doRestoreLesson = async (id) => {
    try { await restoreLesson(id); toast.success('Занятие восстановлено'); await loadDeleted(); await load(); }
    catch { toast.error('Ошибка восстановления'); }
  };

  // Клик после перетаскивания подавляем (чтобы drag не открывал редактор / не сворачивал модуль)
  const suppressClick = useRef(false);
  const toggle = useCallback((id) => { if (suppressClick.current) return; setExpanded(prev => ({ ...prev, [id]: !prev[id] })); }, []);
  const selectLesson = useCallback((sectionId, lessonId) => {
    if (suppressClick.current) return;
    setExpanded(prev => ({ ...prev, [sectionId]: true })); setSelected({ sectionId, lessonId });
  }, []);

  const onAddLesson = useCallback(async (sectionId) => {
    setExpanded(prev => ({ ...prev, [sectionId]: true }));
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = isHw
        ? await createLesson({ sectionId, title: 'Новое ДЗ', isHomeworkOnly: true })
        : await createLesson({ sectionId, title: 'Новый урок', date: today, time: '18:00' });
      await load();
      if (res.data?.id) setSelected({ sectionId, lessonId: res.data.id });
      toast.success(isHw ? 'ДЗ добавлено' : 'Урок добавлен');
    } catch { toast.error('Ошибка при создании'); }
  }, [isHw, load]);

  const openAddSection = () => { setSecName(''); setSecModal(true); };
  const submitSection = async () => {
    if (!secName.trim()) { toast.error('Введите название раздела'); return; }
    try {
      const r = await createSection({ name: secName.trim(), courseId }); toast.success('Раздел создан');
      if (r?.data?.id) setExpanded(prev => ({ ...prev, [r.data.id]: true }));
      setSecModal(false); load();
    } catch { toast.error('Ошибка при сохранении'); }
  };
  const removeSection = useCallback(async (s) => {
    if (await confirmDialog({ title: 'Удалить модуль?', message: 'Модуль можно будет восстановить в режиме восстановления.', confirmText: 'Удалить' })) {
      try { await deleteSection(s.id); toast.success('Модуль удалён'); setSelected(cur => cur?.sectionId === s.id ? null : cur); load(); }
      catch { toast.error('Ошибка при удалении'); }
    }
  }, [load]);
  const removeLesson = useCallback(async (l) => {
    if (await confirmDialog({ title: 'Удалить занятие?', message: 'Можно восстановить в режиме восстановления.', confirmText: 'Удалить' })) {
      try { await deleteLesson(l.id); setSelected(cur => cur?.lessonId === l.id ? null : cur); toast.success('Удалено'); load(); }
      catch { toast.error('Ошибка при удалении'); }
    }
  }, [load]);

  // ── Меню-действия (три точки) ──
  const openRename = useCallback((kind, item) => { setRenameTarget({ kind, id: item.id }); setRenameVal(kind === 'section' ? item.name : item.title); }, []);
  const submitRename = async () => {
    const v = renameVal.trim(); if (!v || !renameTarget) { setRenameTarget(null); return; }
    try {
      if (renameTarget.kind === 'section') await updateSection(renameTarget.id, { name: v });
      else await updateLesson(renameTarget.id, { title: v });
      setRenameTarget(null); load();
    } catch { toast.error('Ошибка'); }
  };
  const toggleSectionPub = useCallback(async (s) => { try { await updateSection(s.id, { isPublished: !s.isPublished }); toast.success(s.isPublished ? 'Модуль снят с публикации' : 'Модуль опубликован'); load(); } catch { toast.error('Ошибка'); } }, [load]);
  const toggleLessonPub = useCallback(async (l) => { try { await updateLesson(l.id, { isPublished: !l.isPublished }); toast.success(l.isPublished ? 'Снято с публикации' : 'Опубликовано'); load(); } catch { toast.error('Ошибка'); } }, [load]);
  const dupSection = useCallback(async (s) => { try { await duplicateSection(s.id); toast.success('Модуль дублирован'); load(); loadHomeworks(); } catch { toast.error('Ошибка'); } }, [load, loadHomeworks]);
  // Отметить модуль «текущим» (единственный в курсе) / снять отметку.
  const makeCurrent = useCallback(async (s) => {
    try { await makeCurrentSection(s.id, !s.isCurrent); toast.success(!s.isCurrent ? 'Модуль отмечен текущим' : 'Отметка снята'); load(); }
    catch { toast.error('Ошибка'); }
  }, [load]);
  // Двигаем модуль вверх/вниз кнопками (перетаскивание модулей убрано).
  const moveSection = useCallback((s, dir) => {
    setSections(prev => {
      const idx = prev.findIndex(x => x.id === s.id);
      const j = idx + dir;
      if (idx < 0 || j < 0 || j >= prev.length) return prev;
      const next = arrayMove(prev, idx, j);
      reorderSections(next.map(x => x.id)).catch(() => { toast.error('Не удалось сохранить порядок'); load(); });
      return next;
    });
  }, [load]);
  const dupLesson = useCallback(async (l, sectionId) => { try { await duplicateLesson(l.id, sectionId || l.sectionId); toast.success('Дублировано'); load(); loadHomeworks(); } catch { toast.error('Ошибка'); } }, [load, loadHomeworks]);
  const pasteInto = useCallback(async (sectionId) => {
    if (!clipboard) return;
    try {
      if (clipboard.kind === 'lesson') await duplicateLesson(clipboard.id, sectionId);
      else await duplicateSection(clipboard.id);
      toast.success('Вставлено'); load(); loadHomeworks();
    } catch { toast.error('Ошибка вставки'); }
  }, [clipboard, load, loadHomeworks]);

  const buildSecMenu = useCallback((s) => ro ? [] : [
    { label: 'Переименовать', icon: <IcoEdit />, onClick: () => openRename('section', s) },
    { label: 'Вверх', icon: <IcoUp />, onClick: () => moveSection(s, -1) },
    { label: 'Вниз', icon: <IcoDown />, onClick: () => moveSection(s, 1) },
    ...(!isHw ? [{ label: s.isCurrent ? 'Убрать «Текущий»' : 'Сделать текущим', icon: <IcoFlag />, onClick: () => makeCurrent(s) }] : []),
    { label: s.isPublished ? 'Снять с публикации' : 'Опубликовать', icon: <IcoCloud />, onClick: () => toggleSectionPub(s) },
    { label: 'Дублировать', icon: <IcoDup />, onClick: () => dupSection(s) },
    { label: 'Копировать', icon: <IcoCopy />, onClick: () => { setClipboard({ kind: 'section', id: s.id, title: s.name }); toast('Модуль скопирован', { icon: '📋' }); } },
    { label: 'Вставить', icon: <IcoPaste />, disabled: !clipboard, onClick: () => pasteInto(s.id) },
    { label: 'Удалить', icon: <IcoTrash />, danger: true, onClick: () => removeSection(s) },
  ], [ro, isHw, clipboard, openRename, moveSection, makeCurrent, toggleSectionPub, dupSection, pasteInto, removeSection]);
  const buildLesMenu = useCallback((l, sectionId) => ro ? [] : [
    { label: 'Переименовать', icon: <IcoEdit />, onClick: () => openRename('lesson', l) },
    { label: l.isPublished ? 'Снять с публикации' : 'Опубликовать', icon: <IcoCloud />, onClick: () => toggleLessonPub(l) },
    { label: 'Дублировать', icon: <IcoDup />, onClick: () => dupLesson(l, sectionId) },
    { label: 'Копировать', icon: <IcoCopy />, onClick: () => { setClipboard({ kind: 'lesson', id: l.id, title: l.title }); toast('Занятие скопировано', { icon: '📋' }); } },
    { label: 'Вставить', icon: <IcoPaste />, disabled: !clipboard || clipboard.kind !== 'lesson', onClick: () => pasteInto(sectionId) },
    { label: 'Удалить', icon: <IcoTrash />, danger: true, onClick: () => removeLesson(l) },
  ], [ro, clipboard, openRename, toggleLessonPub, dupLesson, pasteInto, removeLesson]);

  const onLessonSaved = async (info) => {
    await load();
    if (info?.created && info?.id && selected) setSelected({ sectionId: selected.sectionId, lessonId: info.id });
  };
  const onLessonDeleted = async () => { setSelected(null); await load(); };

  // номера ДЗ по урокам (мемо — стабильные ссылки, чтобы memo уроков держался)
  const { hwCount, hwByLesson } = useMemo(() => {
    const hwCount = {}, hwByLesson = {};
    if (isHw) for (const hw of homeworks) { hwCount[hw.lessonId] = (hwCount[hw.lessonId] || 0) + 1; (hwByLesson[hw.lessonId] = hwByLesson[hw.lessonId] || []).push(hw); }
    return { hwCount, hwByLesson };
  }, [homeworks, isHw]);

  // список вебинаров курса (для связки ДЗ ↔ урок) + карта дат уроков
  const { webinars, lessonDateById } = useMemo(() => {
    const webinars = [], lessonDateById = {};
    for (const s of sections) for (const l of (s.Lessons || [])) {
      lessonDateById[l.id] = { date: l.date, time: l.time };
      if (!l.isHomeworkOnly) webinars.push({ id: l.id, title: l.title, section: s.name });
    }
    return { webinars, lessonDateById };
  }, [sections]);

  const sectionMatches = (s) => {
    if (!q) return true;
    if ((s.name || '').toLowerCase().includes(q)) return true;
    return (s.Lessons || []).some(l => (isHw ? l.isHomeworkOnly : !l.isHomeworkOnly) && (l.title || '').toLowerCase().includes(q));
  };
  const shownSections = sections.filter(sectionMatches);

  const selSection = selected ? sections.find(s => s.id === selected.sectionId) : null;
  const selLesson = selected && selected.lessonId !== NEW ? (selSection?.Lessons || []).find(l => l.id === selected.lessonId) : null;
  const showLessonEditor = !isHw && selected && (selected.lessonId === NEW || selLesson);
  const showHwPane = isHw && selLesson;

  // ─────────── Drag-and-drop (@dnd-kit): модули + перенос уроков между модулями ───────────
  const sectionsRef = useRef(sections); sectionsRef.current = sections;
  const expandedRef = useRef(expanded); expandedRef.current = expanded;
  const snapshotRef = useRef(null);
  const movedRef = useRef(false);
  const [activeDrag, setActiveDrag] = useState(null);
  const dragDisabled = !!q || ro;   // в режиме просмотра порядок не меняем

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Перетаскиваются только уроки (модули двигаются кнопками) — простая детекция.
  const collisionDetection = closestCenter;

  const sectionOfLesson = (secs, lessonId) => secs.find(s => (s.Lessons || []).some(l => l.id === lessonId));

  const moveLesson = (secs, lessonId, targetSectionId, overLessonId) => {
    const src = sectionOfLesson(secs, lessonId);
    const tgt = secs.find(s => s.id === targetSectionId);
    if (!src || !tgt) return secs;
    const lesson = src.Lessons.find(l => l.id === lessonId);
    const targetIds = (tgt.Lessons || []).map(l => l.id);
    let insertAt = overLessonId != null ? targetIds.indexOf(overLessonId) : -1;
    if (insertAt < 0) insertAt = targetIds.length;
    if (src.id === tgt.id) {
      const curIdx = targetIds.indexOf(lessonId);
      if (curIdx === insertAt || curIdx + 1 === insertAt) return secs;
    }
    return secs.map(s => {
      if (s.id !== src.id && s.id !== tgt.id) return s;
      let lessons = (s.Lessons || []).filter(l => l.id !== lessonId);
      if (s.id === tgt.id) {
        const ids = lessons.map(l => l.id);
        let at = overLessonId != null ? ids.indexOf(overLessonId) : -1;
        if (at < 0) at = lessons.length;
        lessons = [...lessons.slice(0, at), lesson, ...lessons.slice(at)];
      }
      return { ...s, Lessons: lessons };
    });
  };

  const handleDragStart = (event) => {
    const { active } = event;
    snapshotRef.current = sectionsRef.current;
    movedRef.current = false;
    suppressClick.current = false;
    const lid = active.data.current.lessonId;
    const s = sectionOfLesson(sectionsRef.current, lid);
    setActiveDrag({ type: 'lesson', lesson: s?.Lessons.find(l => l.id === lid) });
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over || active.data.current?.type !== 'lesson') return;
    const lessonId = active.data.current.lessonId;
    const overType = over.data.current?.type;
    let targetSectionId = null, overLessonId = null;
    if (overType === 'lesson') {
      overLessonId = over.data.current.lessonId; targetSectionId = over.data.current.sectionId;
      // закрытый модуль (при keepMounted его уроки в DOM, но скрыты) — не цель для дропа
      if (!(q || expandedRef.current[targetSectionId])) return;
    }
    else if (overType === 'section') {
      targetSectionId = over.data.current.sectionId;
      // В ЗАКРЫТЫЙ модуль переносить нельзя — пропускаем его (перенос только в открытый).
      if (!(q || expandedRef.current[targetSectionId])) return;
    }
    else return;
    if (targetSectionId == null) return;
    // Внутри того же модуля НЕ трогаем стейт — sortable сам плавно анимирует, реордер применим в onDragEnd.
    const activeContainer = sectionOfLesson(sectionsRef.current, lessonId)?.id;
    if (activeContainer === targetSectionId) return;
    setSections(prev => {
      const next = moveLesson(prev, lessonId, targetSectionId, overLessonId);
      if (next !== prev) movedRef.current = true;
      return next;
    });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    const type = activeDrag?.type;
    setActiveDrag(null);
    suppressClick.current = true; setTimeout(() => { suppressClick.current = false; }, 80);
    if (!over) { if (snapshotRef.current) setSections(snapshotRef.current); return; }

    if (type === 'lesson') {
      const lessonId = active.data.current.lessonId;
      const overType = over.data.current?.type;
      let next = sectionsRef.current;
      // Финальный реордер внутри модуля (onDragOver его не делал — ради плавной анимации).
      if (overType === 'lesson') {
        const overLessonId = over.data.current.lessonId;
        const activeContainer = sectionOfLesson(next, lessonId)?.id;
        const overContainer = sectionOfLesson(next, overLessonId)?.id;
        if (activeContainer != null && activeContainer === overContainer && lessonId !== overLessonId) {
          next = next.map(s => {
            if (s.id !== activeContainer) return s;
            const ids = s.Lessons.map(l => l.id);
            const from = ids.indexOf(lessonId), to = ids.indexOf(overLessonId);
            if (from < 0 || to < 0) return s;
            return { ...s, Lessons: arrayMove(s.Lessons, from, to) };
          });
          setSections(next);
          movedRef.current = true;
        }
      }
      if (!movedRef.current) return;
      const tgt = sectionOfLesson(next, lessonId);
      if (!tgt) return;
      const lesson = tgt.Lessons.find(l => l.id === lessonId);
      const crossSection = lesson.sectionId !== tgt.id;
      const visibleIds = (tgt.Lessons || []).filter(l => isHw ? l.isHomeworkOnly : !l.isHomeworkOnly).map(l => l.id);
      try {
        if (crossSection) {
          await updateLesson(lessonId, { sectionId: tgt.id });
          setSections(prev => prev.map(s => ({ ...s, Lessons: (s.Lessons || []).map(l => l.id === lessonId ? { ...l, sectionId: tgt.id } : l) })));
        }
        await reorderLessons(visibleIds);
      } catch { toast.error('Не удалось сохранить'); load(); }
    }
  };

  const handleDragCancel = () => {
    setActiveDrag(null);
    suppressClick.current = true; setTimeout(() => { suppressClick.current = false; }, 80);
    if (snapshotRef.current) setSections(snapshotRef.current);
  };

  // Группировка отдельно удалённых уроков по их (живому) модулю — для иерархии в восстановлении
  const deletedLessonGroups = () => {
    const map = new Map();
    for (const l of (deleted.lessons || [])) {
      const sid = l.Section?.id ?? 0; const sname = l.Section?.name || 'Модуль';
      if (!map.has(sid)) map.set(sid, { id: sid, name: sname, lessons: [] });
      map.get(sid).lessons.push(l);
    }
    return [...map.values()];
  };

  if (loading) return <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-3)', fontSize: 14 }}>Загрузка…</div>;

  const moduleList = (
    <>
      {shownSections.map((s, i) => (
        <SectionNode key={s.id}
          section={s} isHw={isHw}
          open={q ? true : !!expanded[s.id]}
          onToggle={toggle}
          selected={selected}
          onSelectLesson={selectLesson}
          onAddLesson={onAddLesson}
          hwCount={hwCount}
          lessonDateById={lessonDateById}
          sectionIcon={sectionIcon}
          q={q}
          dragDisabled={dragDisabled}
          readOnly={ro}
          buildSecMenu={buildSecMenu}
          buildLesMenu={buildLesMenu}
        />
      ))}
    </>
  );

  return (
    <div className="cc-shell cc-shell-wrap">
      <ShellCover id={courseId} />
      {!q && (
        <div className="cc-shell-head">
          <div className="cc-topcard-main">
            <input className="cc-topcard-name" value={courseName} onChange={e => setCourseName(e.target.value)}
              onBlur={renameCourse} onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} placeholder="Название курса"
              disabled={restoreMode || ro} />
            <div className="cc-topcard-sub">{restoreMode ? 'Режим восстановления' : (isHw ? 'Задания курса' : 'Вебинары курса')}</div>
          </div>
          {!restoreMode && !ro && (
            <button className="cc-top-ic pub" onClick={publishAll} disabled={!hasDrafts || publishing}
              title={hasDrafts ? 'Опубликовать все занятия' : 'Всё опубликовано'}><IcoCloudUp /></button>
          )}
          {restoreMode ? (
            <button className="cc-top-ic back" onClick={() => setRestoreMode(false)} title="Вернуться к модулям"><IcoArrowRight /></button>
          ) : !ro && (
            <button className="cc-top-ic" onClick={openRestore} title="Восстановление удалённых занятий"><IcoRestore /></button>
          )}
        </div>
      )}
      <div className="cc-wrap">
      <div className="cc-left">
        <div className="cc-swipe" style={{ transform: restoreMode ? 'translateX(-50%)' : 'none' }}>
        {/* Панель восстановления (справа по order; список виден без трансформы — важно для DnD) */}
        <div className="cc-swipe-pane restore">
          <div className="cc-restore">
            <div className="cc-restore-head">
              <div className="t"><IcoRestore /> Режим восстановления</div>
            </div>
            {deleted.sections.length === 0 && deleted.lessons.length === 0 && (
              <div className="cc-empty-leaf" style={{ padding: '22px 8px' }}>Удалённых занятий нет</div>
            )}
            {deleted.sections.map(sec => {
              const open = restoreOpen[`s${sec.id}`] !== false; // по умолчанию раскрыт
              const lessons = sec.Lessons || [];
              return (
                <div key={'s' + sec.id} className={`cc-node${open ? ' open' : ''}`}>
                  <div className="cc-node-head" onClick={() => toggleRestore(`s${sec.id}`)}>
                    <span className="cc-node-ic">{sectionIcon || <IcoLayers />}</span>
                    <div className="cc-node-info">
                      <div className="cc-node-title">{sec.name}</div>
                      <div className="cc-node-sub">Удалённый модуль{lessons.length ? ` · ${lessons.length} ${plural(lessons.length, ['занятие', 'занятия', 'занятий'])}` : ''}</div>
                    </div>
                    <button className="cc-rst-ic" title="Восстановить модуль" onClick={(e) => { e.stopPropagation(); doRestoreSection(sec.id); }}><IcoRestore /></button>
                    <button className="cc-rst-ic del" title="Удалить модуль насовсем" onClick={(e) => { e.stopPropagation(); doPurgeSection(sec); }}><IcoTrash /></button>
                    {lessons.length > 0 && <span className="cc-chev"><IcoChevron /></span>}
                  </div>
                  {lessons.length > 0 && (
                    <Collapse open={open}>
                      <div className="cc-node-body">
                        {lessons.map(l => (
                          <div key={l.id} className="cc-leaf" style={{ cursor: 'default' }}>
                            <span className="cc-date empty">{l.isHomeworkOnly || l.isVariant ? <IcoDoc /> : <IcoCal />}</span>
                            <div className="cc-leaf-main"><div className="cc-leaf-title">{l.title}</div><div className="cc-leaf-sub">вернётся с модулем</div></div>
                          </div>
                        ))}
                      </div>
                    </Collapse>
                  )}
                </div>
              );
            })}
            {deletedLessonGroups().map((g, gi) => {
              const key = `g${g.id ?? gi}`;
              const open = restoreOpen[key] !== false;
              return (
                <div key={key} className={`cc-node${open ? ' open' : ''}`}>
                  <div className="cc-node-head" onClick={() => toggleRestore(key)}>
                    <span className="cc-node-ic">{sectionIcon || <IcoLayers />}</span>
                    <div className="cc-node-info">
                      <div className="cc-node-title">{g.name}</div>
                      <div className="cc-node-sub">{g.lessons.length} {plural(g.lessons.length, ['удалённое занятие', 'удалённых занятия', 'удалённых занятий'])}</div>
                    </div>
                    <span className="cc-chev"><IcoChevron /></span>
                  </div>
                  <Collapse open={open}>
                    <div className="cc-node-body">
                      {g.lessons.map(l => (
                        <div key={l.id} className="cc-leaf" style={{ cursor: 'default' }}>
                          <span className="cc-date empty">{l.isHomeworkOnly || l.isVariant ? <IcoDoc /> : <IcoCal />}</span>
                          <div className="cc-leaf-main"><div className="cc-leaf-title">{l.title}</div></div>
                          <button className="cc-rst-ic" title="Восстановить занятие" onClick={() => doRestoreLesson(l.id)}><IcoRestore /></button>
                          <button className="cc-rst-ic del" title="Удалить занятие насовсем" onClick={() => doPurgeLesson(l)}><IcoTrash /></button>
                        </div>
                      ))}
                    </div>
                  </Collapse>
                </div>
              );
            })}
          </div>
        </div>
        {/* Панель списка модулей */}
        <div className="cc-swipe-pane list">
        <DndContext sensors={sensors} collisionDetection={collisionDetection}
          onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
          <div className="cc-left-scroll">
            {!q ? (
              <div className="cc-shell-body">
                {moduleList}
                {shownSections.length === 0 && <div className="cc-empty-leaf" style={{ padding: '18px 8px' }}>Разделов пока нет</div>}
                <button className="cc-add-section" onClick={openAddSection}><IcoPlus /> Добавить раздел</button>
              </div>
            ) : (
              <>
                {moduleList}
                {shownSections.length === 0 && <div className="cc-empty-leaf" style={{ padding: '18px 8px' }}>Ничего не найдено</div>}
              </>
            )}
          </div>

          <DragOverlay dropAnimation={{ duration: 300, easing: 'cubic-bezier(0.34, 1.25, 0.64, 1)' }}>
            {activeDrag?.type === 'lesson' && activeDrag.lesson && (
              <div className="cc-leaf cc-overlay">
                {!activeDrag.lesson.isPublished
                  ? <LeafCloud />
                  : (isHw && !activeDrag.lesson.linkedLessonId)
                    ? <LeafStar />
                    : <LeafDate dateStr={isHw ? hwDate(activeDrag.lesson, lessonDateById) : activeDrag.lesson.date} />}
                <div className="cc-leaf-main"><div className="cc-leaf-title">{activeDrag.lesson.title}</div></div>
              </div>
            )}
          </DragOverlay>
        </DndContext>
        </div>
        </div>
      </div>

      <div className="cc-right">
        <fieldset disabled={ro} style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: 'contents' }}>
        {showLessonEditor ? (
          <LessonEditorPane
            key={selected.lessonId}
            lesson={selected.lessonId === NEW ? null : selLesson}
            sectionId={selected.sectionId}
            icon={sectionIcon}
            flushRef={editorFlush}
            onSaved={onLessonSaved}
            onDeleted={onLessonDeleted}
          />
        ) : showHwPane ? (
          <HomeworkNumbersPane
            key={selLesson.id}
            lesson={selLesson}
            list={hwByLesson[selLesson.id] || []}
            webinars={webinars}
            icon={sectionIcon}
            onChanged={loadHomeworks}
            onNumberSaved={onNumberSaved}
            onLessonChanged={load}
            onDeleted={onLessonDeleted}
          />
        ) : (
          <div className="cc-placeholder">
            <div className="cc-ph-ic">
              {isHw ? (
                <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
              )}
            </div>
            <div className="cc-ph-title">{isHw ? 'Выберите ДЗ' : 'Выберите урок'}</div>
            <div className="cc-ph-sub">{isHw ? 'Кликните ДЗ слева — справа откроются его номера. Новый ДЗ — кнопкой «Добавить ДЗ» внутри раздела.' : 'Кликните урок слева или добавьте новый — его редактирование откроется здесь.'}</div>
          </div>
        )}
        </fieldset>
      </div>

      <Modal isOpen={secModal} onClose={() => setSecModal(false)} title="Новый раздел">
        <Input label="Название раздела" value={secName} onChange={(e) => setSecName(e.target.value)} required />
        <div className="form-actions">
          <Button variant="secondary" onClick={() => setSecModal(false)}>Отмена</Button>
          <Button variant="purple" onClick={submitSection}>Сохранить</Button>
        </div>
      </Modal>

      <Modal isOpen={!!renameTarget} onClose={() => setRenameTarget(null)} title={renameTarget?.kind === 'section' ? 'Переименовать модуль' : 'Переименовать занятие'}>
        <Input label="Название" value={renameVal} onChange={(e) => setRenameVal(e.target.value)} required />
        <div className="form-actions">
          <Button variant="secondary" onClick={() => setRenameTarget(null)}>Отмена</Button>
          <Button variant="purple" onClick={submitRename}>Сохранить</Button>
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default CourseConstructor;
